/*
 * pt26-core.js — Single JavaScript entrypoint for the new pt26 design system.
 *
 * Loaded once from layout/theme.liquid as <script type="module"> by its asset
 * URL. There is no import map — see componentRegistry for why.
 *
 * Owns three things that everything else depends on:
 *   1. PT26_EVENTS — the frozen product-event name map. Components import the
 *      constant, they never type the string.
 *   2. componentRegistry — maps a <pt26-lazy-mount component="…"> key to the
 *      asset file name of its module, resolved via #pt26-module-urls.
 *   3. <pt26-lazy-mount> — the lazy loader itself. Defined here, never lazy.
 */

/**
 * Product-scoped custom event names. Dispatched on the product root
 * ([data-pt26-product-root]) or document, per docs/pt26-components.md §3.4.
 * Frozen so a typo is a runtime error, not a silently missed listener.
 *
 * @type {Readonly<{variantChange: string, cartUpdated: string, cartDrawerOpen: string}>}
 */
const PT26_EVENTS = Object.freeze({
  variantChange: 'pt26:variant:change',
  cartUpdated: 'pt26:cart:updated',
  cartDrawerOpen: 'pt26:cart-drawer:open',
  collectionPageAppended: 'pt26:collection:page-appended',
});

/**
 * Maps a component name (as used on <pt26-lazy-mount component="…">) to the
 * file name of its module.
 *
 * Deliberately a file name and not a bare specifier: bare names need an import
 * map, and the browser drops an import map as soon as anything else on the page
 * triggers a module load before it — silently. That is how every pt26 component
 * with lazy JS once failed to mount while the page still looked correct, because
 * HTML and CSS carry it without JavaScript.
 */
const componentRegistry = {
  slider: 'pt26-slider.js',
  'multi-column': 'pt26-multi-column.js',
  'accordion': 'pt26-accordion.js',
  'product-price': 'pt26-product-price.js',
  'product-cta': 'pt26-product-cta.js',
  'product-gallery': 'pt26-product-gallery.js',
  'product-ingredients': 'pt26-product-ingredients.js',
  'product-purchase-options': 'pt26-product-purchase-options.js',
  'content-slider': 'pt26-content-slider.js',
  'variant-drawer': 'pt26-variant-drawer.js',
  'bundle-selection': 'pt26-bundle-selection.js',
  'sticky-element': 'pt26-sticky-element.js',
  'cart-notification': 'pt26-cart-notification.js',
  'collection-grid': 'pt26-collection-grid.js',
  'collection-filters': 'pt26-collection-filters.js',
  'product-recommendations': 'pt26-product-recommendations.js',
};

/**
 * Versioned asset URLs, keyed by file name. Liquid writes them into
 * #pt26-module-urls in layout/theme.liquid so a changed component busts its own
 * cache. Read once, on first use.
 *
 * @type {Record<string, string> | null}
 */
let moduleUrls = null;

/**
 * Resolve a module file name to a URL.
 *
 * Prefers the versioned URL from Liquid. Falls back to a path next to this
 * module, which always resolves — worse caching, never a dead component.
 *
 * @param {string} file - Asset file name, e.g. "pt26-slider.js".
 * @returns {string}
 */
function resolveModuleUrl(file) {
  if (moduleUrls === null) {
    const node = document.getElementById('pt26-module-urls');

    try {
      moduleUrls = node ? JSON.parse(node.textContent) : {};
    } catch (error) {
      console.error('[pt26] #pt26-module-urls is not valid JSON', error);
      moduleUrls = {};
    }
  }

  return moduleUrls[file] || new URL('./' + file, import.meta.url).href;
}

/**
 * Import-promise cache, keyed by resolved module URL. A component module is fetched
 * at most once per page regardless of how many instances mount it.
 *
 * @type {Map<string, Promise<unknown>>}
 */
const moduleImports = new Map();

/**
 * Lazy-load a component module by its registered name.
 *
 * Resolves the name through componentRegistry and dynamically imports the
 * module (which registers its custom element), caching the promise so repeated
 * calls share one network request. Resolves to undefined for an unknown name.
 *
 * @param {string} name - The component name used on <pt26-lazy-mount>.
 * @returns {Promise<unknown>}
 */
function loadComponent(name) {
  const file = componentRegistry[name];

  if (!file) {
    return Promise.resolve();
  }

  const url = resolveModuleUrl(file);

  if (!moduleImports.has(url)) {
    // Ein fehlgeschlagener Import blieb hier fruehen still. Jetzt steht er in
    // der Konsole, statt die Komponente lautlos nie zu mounten.
    moduleImports.set(
      url,
      import(url).catch((error) => {
        console.error('[pt26] component module "' + file + '" failed to load', error);
      })
    );
  }

  return moduleImports.get(url);
}

/**
 * <pt26-lazy-mount> — defers importing a component's module until a load
 * strategy fires, then the imported module upgrades the custom element inside.
 *
 * Attributes:
 *   component  registry key (required)
 *   load       eager | visible (default) | idle | interaction
 *
 * Works when inserted after load by the Section Rendering API, because all the
 * work happens in connectedCallback and is torn down in disconnectedCallback.
 */
class Pt26LazyMount extends HTMLElement {
  constructor() {
    super();

    this.hasMounted = false;
    this.observer = null;
    this.idleHandle = null;
    this.timeoutHandle = null;
    this.interactionEvents = ['pointerenter', 'focusin', 'click'];
    this.handleInteraction = this.handleInteraction.bind(this);
  }

  connectedCallback() {
    const component = this.getAttribute('component');

    if (!component) {
      return;
    }

    const strategy = this.getAttribute('load') || 'visible';

    switch (strategy) {
      case 'eager':
        this.mount();
        break;
      case 'idle':
        this.observeIdle();
        break;
      case 'interaction':
        this.observeInteraction();
        break;
      case 'visible':
      default:
        this.observeVisible();
        break;
    }
  }

  disconnectedCallback() {
    this.teardown();
  }

  /**
   * Import the component module once. The module's own customElements.define()
   * upgrades any matching element already inside this mount.
   */
  mount() {
    if (this.hasMounted) {
      return;
    }

    this.hasMounted = true;
    this.teardown();
    loadComponent(this.getAttribute('component'));
  }

  observeVisible() {
    if (!('IntersectionObserver' in window)) {
      this.mount();
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.mount();
        }
      },
      { rootMargin: '300px' }
    );

    this.observer.observe(this);
  }

  observeIdle() {
    if ('requestIdleCallback' in window) {
      this.idleHandle = window.requestIdleCallback(() => this.mount());
    } else {
      this.timeoutHandle = window.setTimeout(() => this.mount(), 200);
    }
  }

  observeInteraction() {
    // Capture phase so the triggering click still reaches its target once the
    // component has upgraded.
    this.interactionEvents.forEach((eventName) => {
      this.addEventListener(eventName, this.handleInteraction, { capture: true, once: true });
    });
  }

  handleInteraction() {
    this.mount();
  }

  /**
   * Release every observer, listener and timer. Safe to call more than once.
   */
  teardown() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    if (this.idleHandle !== null && 'cancelIdleCallback' in window) {
      window.cancelIdleCallback(this.idleHandle);
      this.idleHandle = null;
    }

    if (this.timeoutHandle !== null) {
      window.clearTimeout(this.timeoutHandle);
      this.timeoutHandle = null;
    }

    this.interactionEvents.forEach((eventName) => {
      this.removeEventListener(eventName, this.handleInteraction, { capture: true });
    });
  }
}

if (!customElements.get('pt26-lazy-mount')) {
  customElements.define('pt26-lazy-mount', Pt26LazyMount);
}

export { PT26_EVENTS, componentRegistry, loadComponent };
